
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { formatCurrency } from "../../hooks/Currency";
import ReportPrintButton from "../reports/ReportPrintButton";

//------------------------------------------------
// Reusable Stock Transfer Items Content
//------------------------------------------------
const TransferItemsContent = ({
  items,
  store,
  t,
}) => {
  const transferItems = Array.isArray(items)
    ? items
    : [];

  const money = (value) =>
    formatCurrency(Number(value) || 0, store);

  //------------------------------------------------
  // Calculate Total Quantity and Price
  //------------------------------------------------
  const totalQuantity = transferItems.reduce(
    (sum, item) =>
      sum + Number(item.qty || 0),
    0
  );

  const grandTotal = transferItems.reduce(
    (sum, item) =>
      sum +
      Number(item.qty || 0) *
        Number(item.product?.sellPrice || 0),
    0
  );

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Box>
      {/* Title */}
      <Box
        sx={{
          textAlign: "center",
          mt: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
        >
          {t("transferItems")}
        </Typography>

        <Box
          sx={{
            width: "80%",
            borderBottom: "1px solid #ccc",
            mx: "auto",
            mt: 2,
          }}
        />
      </Box>

      {/* Transfer Items Table */}
      <TableContainer sx={{ mt: 2 }}>
        <Table
          size="small"
          aria-label="stock transfer items"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("itemName")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("quantity")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("unitPrice")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {t("subtotal")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {transferItems.map((item, index) => {
              const quantity =
                Number(item.qty) || 0;

              const unitPrice =
                Number(item.product?.sellPrice) || 0;

              const subtotal =
                quantity * unitPrice;

              return (
                <TableRow key={item.id ?? index}>
                  <TableCell>
                    {item.product?.name || ""}
                  </TableCell>

                  <TableCell align="right">
                    {quantity}
                  </TableCell>

                  <TableCell align="right">
                    {money(unitPrice)}
                  </TableCell>

                  <TableCell align="right">
                    {money(subtotal)}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* Total Row */}
            <TableRow>
              <TableCell
                sx={{ fontWeight: "bold" }}
              >
                {t("total")}
              </TableCell>

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {totalQuantity}
              </TableCell>

              <TableCell />

              <TableCell
                align="right"
                sx={{ fontWeight: "bold" }}
              >
                {money(grandTotal)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

//------------------------------------------------
// View Stock Transfer Items
//------------------------------------------------
const ViewData = (props) => {
  const { t } = useTranslation();

  const { setShowForm, items } = props;

  const _store = useSelector(
    (state) => state.pos.defStore
  );

  const reportRef = useRef(null);

  //------------------------------------------------
  // Render
  //------------------------------------------------
  return (
    <Grid container className="p-20">
      {/* Print Preview Button */}
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 1,
        }}
      >
        <ReportPrintButton
          reportRef={reportRef}
          paperSize="Letter"
          reportTitle={t("transferItems")}
        />
      </Grid>

      {/* Screen Transfer Details */}
      <Grid item xs={12}>
        <TransferItemsContent
          items={items}
          store={_store}
          t={t}
        />
      </Grid>

      {/* Print-only Transfer Details */}
      <Grid item xs={12}>
        <Box
          ref={reportRef}
          className="stock-transfer-details-print"
          sx={{
            position: "absolute",
            left: "-10000px",
            top: 0,
            width: "1000px",
            backgroundColor: "#fff",
            color: "#000",
            p: 2,
          }}
        >
          <TransferItemsContent
            items={items}
            store={_store}
            t={t}
          />
        </Box>
      </Grid>

      {/* Close Button */}
      <Grid
        item
        xs={12}
        className="tx-center mt-40"
      >
        <Button
          onClick={() => setShowForm(false)}
          className="btn-error"
          size="large"
          sx={{ minWidth: "150px" }}
        >
          {t("close")}
        </Button>
      </Grid>
    </Grid>
  );
};

export default ViewData;
